import { baseApi, API_TAGS, createEndpointBuilder } from '@/shared/api';
import type {
  OrganizationListResponse,
  OrganizationsSearchParams,
  Organization,
  OrganizationCreateUpdateRequest,
  OrganizationTypesResponse,
  OrganizationParams
} from '../model/types';

const organizationEndpoint = createEndpointBuilder('/api/v2/organizations');

export const organizationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizations: builder.query<OrganizationListResponse, OrganizationsSearchParams>({
      query: (params) => ({
        url: organizationEndpoint(''),
        params
      }),
      providesTags: (_result, _error, { search }) => [
        API_TAGS.ORGANIZATIONS,
        { type: API_TAGS.ORGANIZATIONS, id: search || 'LIST' }
      ]
    }),

    getOrganization: builder.query<Organization, { id: number } & OrganizationParams>({
      query: ({ id, projectId, siteId }) => ({
        url: organizationEndpoint(`/${id}`),
        method: 'GET',
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, { id }) => [{ type: API_TAGS.ORGANIZATIONS, id }]
    }),

    createOrganization: builder.mutation<Organization, OrganizationCreateUpdateRequest & OrganizationParams>({
      query: ({ projectId, siteId, ...body }) => ({
        url: organizationEndpoint(''),
        method: 'POST',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: [API_TAGS.ORGANIZATIONS]
    }),

    updateOrganization: builder.mutation<
      Organization,
      { id: number } & OrganizationCreateUpdateRequest & OrganizationParams
    >({
      query: ({ id, projectId, siteId, ...body }) => ({
        url: organizationEndpoint(`/${id}`),
        method: 'PUT',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [API_TAGS.ORGANIZATIONS, { type: API_TAGS.ORGANIZATIONS, id }]
    }),

    getOrganizationTypes: builder.query<OrganizationTypesResponse, void>({
      query: () => ({
        url: organizationEndpoint('/types'),
        method: 'GET'
      }),
      providesTags: [API_TAGS.ORGANIZATIONS]
    })
  })
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationTypesQuery
} = organizationsApi;
